import '../styles/wheel-gallery.css';
import LightboxGallery from './lightbox-gallery';

const rootSelector = '[data-wheel-gallery-root]';
const itemSelector = '[data-wheel-gallery-item]';

function initWheelGallery(root) {
    if (root.dataset.wheelGalleryInit) return;
    root.dataset.wheelGalleryInit = 'true';

    const items = Array.from(root.querySelectorAll(itemSelector));
    const total = items.length;
    if (total < 2) return;

    const pan = root.querySelector('[data-wheel-gallery-pan]');
    if (!pan) return;

    const status = root.querySelector('[data-wheel-gallery-status]');
    const allBtn = root.querySelector('[data-wheel-gallery-all]');

    const images = items.map(item => item.querySelector('img')?.dataset.fullSrc || '');
    const captions = items.map(item => item.querySelector('img')?.dataset.caption || '');

    let activeIndex = items.findIndex(item => item.dataset.state === 'active');
    if (activeIndex < 0) activeIndex = 0;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    // Real, per-slot resting positions -- NOT an assumed `index * slotWidth`
    // grid. A scroll-snap-align:center slot's true native resting scrollLeft
    // is `slot.offsetLeft + slot.offsetWidth / 2 - pan.clientWidth / 2`,
    // which only equals `index * slotWidth` when the strip's own width
    // happens to equal one slot's width -- it generally doesn't here, since
    // the (invisible) strip spans the whole stage. Deriving targets from a
    // mismatched assumption is what caused drift that grew with each slide;
    // reading each slot's actual measured offset directly cannot drift,
    // because it's always in exact agreement with wherever the browser's
    // own scroll-snap will natively come to rest.
    let snapOffsets = [];
    let spacing = 1;

    function measure() {
        const slots = Array.from(pan.querySelectorAll('.wg-pan-slot'));
        const containerWidth = pan.clientWidth;
        snapOffsets = slots.map(slot => slot.offsetLeft + slot.offsetWidth / 2 - containerWidth / 2);
        spacing = snapOffsets.length > 1
            ? (snapOffsets[snapOffsets.length - 1] - snapOffsets[0]) / (snapOffsets.length - 1)
            : 1;
    }

    // Every scroll frame: continuous, fractional --distance per item so the
    // rotation/opacity/shadow crossfade mid-drag rather than snapping between
    // discrete states. CSS owns all the resulting transform/opacity math.
    function updateDistances() {
        items.forEach((item, i) => {
            const distance = spacing ? (snapOffsets[i] - pan.scrollLeft) / spacing : 0;
            const absDistance = Math.abs(distance);
            item.style.setProperty('--distance', distance);
            item.style.setProperty('--abs-distance', absDistance);
            item.dataset.state = absDistance < 0.5 ? 'active' : absDistance < 1.5 ? 'adjacent' : 'hidden';
        });
    }

    // The visible caption below the stage is a single, static gallery-level
    // field (matches Figma/anatomy) -- distinct from each slide's own
    // data-caption, which only feeds the lightbox. Only the a11y status
    // region reflects which slide is active.
    function render() {
        if (status) status.textContent = `Showing slide ${activeIndex + 1} of ${total}`;
    }

    function onSettle() {
        let nearest = 0;
        let nearestDelta = Infinity;
        snapOffsets.forEach((offset, i) => {
            const delta = Math.abs(offset - pan.scrollLeft);
            if (delta < nearestDelta) {
                nearestDelta = delta;
                nearest = i;
            }
        });
        activeIndex = nearest;
        updateDistances();
        render();
    }

    function goTo(index) {
        const target = ((index % total) + total) % total;
        pan.scrollTo({ left: snapOffsets[target], behavior });
    }

    function openLightbox() {
        LightboxGallery.open(images, captions, activeIndex);
    }

    // rAF-throttled scroll -> continuous distance update, mirroring the
    // pan-capture pattern in premium-slider.js. A debounce stands in for
    // `scrollend` (used directly where supported) to detect when the strip
    // has settled on a slide.
    let raf = null;
    let settleTimeout = null;

    pan.addEventListener('scroll', () => {
        if (!raf) {
            raf = window.requestAnimationFrame(() => {
                updateDistances();
                raf = null;
            });
        }
        clearTimeout(settleTimeout);
        settleTimeout = setTimeout(onSettle, 150);
    });

    if ('onscrollend' in window) {
        pan.addEventListener('scrollend', () => {
            clearTimeout(settleTimeout);
            onSettle();
        });
    }

    // Pointer/touch interaction: let the pan strip capture the gesture, then
    // pass click hit-testing through to whatever sits underneath it.
    pan.addEventListener('click', event => {
        pan.style.pointerEvents = 'none';
        const hit = document.elementFromPoint(event.clientX, event.clientY);
        pan.style.pointerEvents = '';
        const item = hit && hit.closest(itemSelector);
        if (!item) return;
        const index = items.indexOf(item);
        if (index === activeIndex) {
            openLightbox();
        } else {
            goTo(index);
        }
    });

    pan.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(activeIndex + 1);
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(activeIndex - 1);
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openLightbox();
        }
    });

    // Keyboard/assistive-tech path, independent of the pan strip's pointer
    // routing above (a synthetic click on a focused button reaches it
    // directly regardless of the pan strip sitting visually on top).
    items.forEach((item, index) => {
        const select = item.querySelector('.wg-select');
        if (!select) return;
        select.addEventListener('click', () => {
            if (index === activeIndex) {
                openLightbox();
            } else {
                goTo(index);
            }
        });
    });

    if (allBtn) {
        allBtn.addEventListener('click', () => openLightbox());
    }

    window.addEventListener('resize', () => {
        // --wheel-active-w (and so each slot's real offset) changes at the
        // 1024px breakpoint; remeasure and re-sync instantly to the settled
        // index rather than animating.
        measure();
        pan.scrollLeft = snapOffsets[activeIndex];
        updateDistances();
    });

    measure();
    pan.scrollLeft = snapOffsets[activeIndex];
    updateDistances();
    render();
}

function init() {
    document.querySelectorAll(rootSelector).forEach(initWheelGallery);
}

export default {
    init,
    initWheelGallery
};
